/**
 * Type-level conversion from snake_case to camelCase
 */
type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

/**
 * Type-level conversion from camelCase to snake_case
 */
type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? U extends Uncapitalize<U>
    ? `${Lowercase<T>}${CamelToSnakeCase<U>}`
    : `${Lowercase<T>}_${CamelToSnakeCase<Uncapitalize<U>>}`
  : S;

/**
 * Recursively transforms all keys in an object from snake_case to camelCase at the type level
 */
type SnakeToCamel<T> =
  T extends Array<infer U>
    ? Array<SnakeToCamel<U>>
    : T extends object
      ? {
          [K in keyof T as SnakeToCamelCase<K & string>]: SnakeToCamel<T[K]>;
        }
      : T;

/**
 * Recursively transforms all keys in an object from camelCase to snake_case at the type level
 */
type CamelToSnake<T> =
  T extends Array<infer U>
    ? Array<CamelToSnake<U>>
    : T extends object
      ? {
          [K in keyof T as CamelToSnakeCase<K & string>]: CamelToSnake<T[K]>;
        }
      : T;

/**
 * Converts a snake_case string to camelCase
 */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/**
 * Converts a camelCase string to snake_case
 */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter, index) =>
    index === 0 ? letter.toLowerCase() : `_${letter.toLowerCase()}`,
  );
}

/**
 * Recursively transforms object keys from snake_case to camelCase
 *
 * This utility is used to transform data received from the backend (snake_case)
 * to match TypeScript interface expectations (camelCase) before Zod validation.
 *
 * The return type accurately reflects the transformed keys at the type level,
 * providing full type safety.
 *
 * @param input - The data to transform (object, array, or primitive)
 * @returns The transformed data with camelCase keys (typed correctly)
 *
 * @example
 * ```typescript
 * // Transform a single object
 * const snakeData = { user_id: "123", image_url: "https://..." }
 * const camelData = snakeToCamel(snakeData)
 * // Type: { userId: string, imageUrl: string }
 * console.log(camelData.userId) // ✓ Type-safe!
 *
 * // Transform an array of objects
 * const users = [{ user_id: "1" }, { user_id: "2" }]
 * const camelUsers = snakeToCamel(users)
 * // Type: Array<{ userId: string }>
 * ```
 */
export function snakeToCamel<T>(input: T): SnakeToCamel<T> {
  if (input === null || input === undefined) {
    return input as SnakeToCamel<T>;
  }

  // Handle primitives (string, number, boolean)
  if (typeof input !== "object") {
    return input as SnakeToCamel<T>;
  }

  if (Array.isArray(input)) {
    return input.map((item: unknown) => snakeToCamel(item)) as SnakeToCamel<T>;
  }

  const result: Record<string, unknown> = {};

  for (const key in input) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      const camelKey = toCamelCase(key);
      result[camelKey] = snakeToCamel((input as Record<string, unknown>)[key]);
    }
  }

  return result as SnakeToCamel<T>;
}

/**
 * Recursively transforms object keys from camelCase to snake_case
 *
 * This utility is used to transform data being sent to the backend (camelCase)
 * to match backend expectations (snake_case).
 *
 * The return type accurately reflects the transformed keys at the type level,
 * providing full type safety.
 *
 * @param input - The data to transform (object, array, or primitive)
 * @returns The transformed data with snake_case keys (typed correctly)
 *
 * @example
 * ```typescript
 * // Transform a single object
 * const camelData = { userId: "123", imageUrl: "https://..." }
 * const snakeData = camelToSnake(camelData)
 * // Type: { user_id: string, image_url: string }
 * console.log(snakeData.user_id) // ✓ Type-safe!
 *
 * // Transform an array of objects
 * const users = [{ userId: "1" }, { userId: "2" }]
 * const snakeUsers = camelToSnake(users)
 * // Type: Array<{ user_id: string }>
 * ```
 */
export function camelToSnake<T>(input: T): CamelToSnake<T> {
  if (input === null || input === undefined) {
    return input as CamelToSnake<T>;
  }

  if (typeof input !== "object") {
    return input as CamelToSnake<T>;
  }

  if (Array.isArray(input)) {
    return input.map((item: unknown) => camelToSnake(item)) as CamelToSnake<T>;
  }

  const result: Record<string, unknown> = {};

  for (const key in input) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      const snakeKey = toSnakeCase(key);
      result[snakeKey] = camelToSnake((input as Record<string, unknown>)[key]);
    }
  }

  return result as CamelToSnake<T>;
}
